<template>
    <v-card>
        <v-card-title class="blue-grey lighten-5 primary--text">
            {{ t('translationModal.title') }}
        </v-card-title>

        <v-form ref="post_defecation_form">

            <v-card-text class="py-3">

                <div class="d-flex justify-center" style="border: 1px solid #a9a9a9; padding: 8px; border-radius: 8px;">
                    <span style="font-size: 16px;">English</span>
                </div>

                <div class="d-flex justify-center" style="margin: 8px 0 8px 0;">
                    <v-icon style="font-size: 56px; text-align: center;">mdi-arrow-down-thin</v-icon>
                </div>

                <v-select v-model="state" :items="choices" :rules="[rules.required]" outlined dense></v-select>
            </v-card-text>

            <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn @click="close" color="error" text>
                    {{ t('translationModal.cancel') }}
                </v-btn>
                <v-spacer></v-spacer>
                <v-btn @click="clickPost" :loading="btnLoading" color="primary" :disabled="state === ''">
                    {{ t('translationModal.submit') }}
                </v-btn>
                <v-spacer></v-spacer>
            </v-card-actions>

        </v-form>

    </v-card>
</template>
<script lang="ts">
import { defineComponent } from 'vue';
import dayjs from 'dayjs';
import rules from '@/service/ValidRules';
import { t } from '@/i18n';

import ApiTranslation from '@/api/ApiTranslation';
import { Toast } from '@/service/SweetAlert';

const TRANSLATION_CACHE_PREFIX = 'translation_';
const TRANSLATION_DEFAULT_KEY = 'translation_default';
const TRANSLATION_LAST_CHAT_ID_KEY = 'translation_chatid';
const MAX_CACHE_ENTRIES = 100;

interface DataType {
    state: string;
    btnLoading: boolean;
    choices: Array<{
        title: string;
        value: string;
    }>;
}

export default defineComponent({
    name: 'TranslationModal',

    props: {
        isOpen: {
            type: Boolean,
            default: false,
        },
        childId: {
            type: Number,
            default: NaN,
        },
        selectedChat: {
            type: Object,
            default: null,
        },
    },

    computed: {
        rules() {
            return rules;
        },
        form(): any {
            return (this.$refs as any).post_defecation_form;
        },
    },

    data: (): DataType => {
        const choices = [
            {
                title:'ខ្មែរ',
                value: 'KM',
            },
            {
                title:'日本語',
                value: 'JA',
            },
            {
                title:'한국어',
                value: 'KO',
            },
            {
                title:'中文',
                value: 'ZH',
            },
            {
                title:'Tiếng Việt',
                value: 'VI',
            },
            {
                title:'हिंदी',
                value: 'HI',
            },
            {
                title:'Русский',
                value: 'RU',
            },
            {
                title:'नेपाली',
                value: 'NE',
            },
            {
                title:'မြန်မာ',
                value: 'MY',
            },
            {
                title:'বাংলা',
                value: 'BN',
            },
            {
                title:'Монгол',
                value: 'MN',
            },
            {
                title:'Tagalog',
                value: 'TL',
            },
            {
                title:'Bahasa Melayu',
                value: 'MS',
            },
            {
                title:'English',
                value: 'EN',
            },
        ];

        const defaultLang = localStorage.getItem(TRANSLATION_DEFAULT_KEY)?.toUpperCase() ?? '';
        const state = choices.some((choice) => choice.value === defaultLang) ? defaultLang : 'KM';

        return {
            state,
            btnLoading: false,
            choices,
        };
    },

    methods: {
        t,
        close() {
            this.$emit('close');
        },
        getCacheKey(chatId: number): string {
            return `${TRANSLATION_CACHE_PREFIX}${chatId}`;
        },
        getCachedTranslation(chatId: number, lang: string): string | null {
            try {
                const raw = localStorage.getItem(this.getCacheKey(chatId));
                if (!raw) return null;
                const cache = JSON.parse(raw);
                const text = cache.translations?.[lang] ?? null;
                if (text) {
                    // 利用したエントリの日時を更新し、削除対象になりにくくする
                    cache.updatedAt = Date.now();
                    localStorage.setItem(this.getCacheKey(chatId), JSON.stringify(cache));
                }
                return text;
            } catch {
                return null;
            }
        },
        saveCachedTranslation(chatId: number, lang: string, text: string) {
            try {
                const raw = localStorage.getItem(this.getCacheKey(chatId));
                const parsed = raw ? JSON.parse(raw) : null;
                const translations = parsed?.translations ?? {};
                translations[lang] = text;
                localStorage.setItem(
                    this.getCacheKey(chatId),
                    JSON.stringify({ translations, updatedAt: Date.now() }),
                );
                this.pruneTranslationCache();
            } catch (error) {
                console.error('Translation cache save error:', error);
            }
        },
        pruneTranslationCache() {
            const entries: Array<{ key: string; updatedAt: number }> = [];
            for (let i = 0; i < localStorage.length; i++) {
                const key = localStorage.key(i);
                if (!key || !key.startsWith(TRANSLATION_CACHE_PREFIX)) continue;
                let updatedAt = 0;
                try {
                    updatedAt = JSON.parse(localStorage.getItem(key) ?? '{}').updatedAt ?? 0;
                } catch {
                    // 壊れたエントリは最古扱いで削除対象にする
                }
                entries.push({ key, updatedAt });
            }
            if (entries.length <= MAX_CACHE_ENTRIES) return;
            entries.sort((a, b) => a.updatedAt - b.updatedAt);
            entries
                .slice(0, entries.length - MAX_CACHE_ENTRIES)
                .forEach((entry) => localStorage.removeItem(entry.key));
        },
        async clickPost() {
            if (this.form.validate() && this.selectedChat) {
                this.btnLoading = true;
                const targetLang = this.state.toLowerCase();
                const cacheLang = `auto-${targetLang}`;
                const chatId = this.selectedChat.chatId;
                try {
                    const cached = this.getCachedTranslation(chatId, cacheLang);
                    if (cached) {
                        localStorage.setItem(TRANSLATION_LAST_CHAT_ID_KEY, String(chatId));
                        this.$emit('translate', this.selectedChat, cached);
                        Toast.fire({
                            icon: 'success',
                            title: this.t('translationModal.success'),
                        });
                        this.$emit('close');
                        return;
                    }

                    const response = await ApiTranslation.translateText({
                        text: this.selectedChat.body,
                        // sourceLanguage は指定せず自動識別
                        targetLanguage: targetLang,
                    });

                    this.saveCachedTranslation(chatId, cacheLang, response.translatedText);
                    localStorage.setItem(TRANSLATION_DEFAULT_KEY, targetLang);
                    localStorage.setItem(TRANSLATION_LAST_CHAT_ID_KEY, String(chatId));
                    this.$emit('translate', this.selectedChat, response.translatedText);
                    Toast.fire({
                        icon: 'success',
                        title: this.t('translationModal.success'),
                    });
                    this.$emit('close');
                } catch (error) {
                    console.error('Translation error:', error);
                    Toast.fire({
                        icon: 'error',
                        title: this.t('translationModal.failure'),
                    });
                } finally {
                    this.btnLoading = false;
                }
            }
        },
    },
});
</script>
<style lang="scss" scoped></style>
